import { IconTextDate, Separator, TitleDescription } from "components/custom"
import { Button, DataTable, DateFormat, Icon, Modal } from "components/common"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLazyGetLeadReviewsQuery } from "features/leads/api"
import { ratingArray } from "constants/list"
import { joinBytItems, localisedValue } from "utils"
import { useTranslation } from "react-i18next"
import theme from "utils/themeVars"
import { classifications } from "features/shared/data"
import FlaggedLeads from "../flaggedLeads/flaggedLeads"
import BreakdownModal from "./breakdownModal"

const LeadReviewTable = ({ lead, client, getCount = () => {} }) => {
  const { t } = useTranslation()
  const columns = [
    { title: t("Lead"), dataIndex: "lead", width: 80 },
    { title: t("Type"), dataIndex: "type", width: 80 },
    { title: t("Reviewer"), dataIndex: "reviewer", width: 80 },
    { title: t("Feedback"), dataIndex: "feedback", width: 200 },
  ]

  const [getLeadReviews, { data: reviews, isFetching, error, refetch }] = useLazyGetLeadReviewsQuery()
  const [queryObject, setQueryObject] = useState({
    inquiryIds: lead?.id,
    clientIds: client?.id,
    page: 0,
    pageLimit: 10,
  })
  const [transcription, setTranscription] = useState()
  const modalRef = useRef()
  const breakdownRef = useRef()

  useEffect(() => {
    getLeadReviews({ ...queryObject })
  }, [queryObject])
  useEffect(() => {
    reviews?.count && getCount(reviews?.count)
  }, [reviews?.count])

  const onChangePage = (page, pageSize) => {
    setQueryObject(prev => ({ ...prev, pageLimit: pageSize, page: page }))
  }
  const ratingObject = useMemo(() => {
    const result = {}
    ratingArray().forEach(e => {
      result[e.id] = e
    })
    return result
  }, [ratingArray])

  const tableData = useMemo(() => {
    return reviews?.data?.map((e, index) => {
      return {
        key: index,
        lead: (
          <IconTextDate
            message={classifications(true)[e?.classificationKey]?.name}
            iconProps={{
              color: classifications(true)[e?.classificationKey]?.style?.color,
              hasBackground: true,
            }}
            icon={"MdLocalFireDepartment"}
            flagIcon={e?.isFlagged ? <FlaggedLeads lead={e?.lead} /> : null}
            title={e?.InternalInquiry?.id}
            date={e?.leadCreated}
            customTooltip={t(`Lead Added on`) + ": "}
          />
        ),
        type: (
          <TitleDescription
            title={
              <>
                {e?.reviewType}{" "}
                {e?.showAlertIcon && (
                  <Icon style={{ "--flip-icon": "scaleX(-1)" }} icon="GoAlertFill" color="red" size="16px" />
                )}
              </>
            }
            description={<DateFormat date={e?.reviewCreated} customTooltip={t("Review Added on") + ": "} />}
          />
        ),
        reviewer: (
          <TitleDescription
            gapSpace={2}
            title={e?.reviewer?.name}
            description={joinBytItems([e?.reviewer?.city, e?.reviewer?.designation], <Separator />)}
          />
        ),
        feedback: (
          <>
            <TitleDescription
              title={e?.feedback}
              description={
                <Button
                  type="link"
                  size="small"
                  className="pointer text-underline p-0"
                  style={{ height: "auto" }}
                  onClick={() => breakdownRef?.current?.showFor(e)}
                  text={t("View Breakdown")}
                />
              }
            />
          </>
        ),
      }
    })
  }, [reviews?.data, t, theme])

  return (
    <>
      <DataTable
        pagination={{ totalCount: reviews?.count, pageOptions: [10, 25, 50] }}
        columns={columns}
        data={tableData || []}
        loading={isFetching}
        noUrlPush={true}
        error={error}
        onErrorRetry={() => getLeadReviews({ ...queryObject })}
        stickyOffset={true}
        onChangePage={onChangePage}
      />
      <Modal
        title={t("Meeting Review Transcription")}
        ref={modalRef}
        width={1000}
        onCancel={() => {
          setTranscription(null)
          modalRef?.current?.setVisible(false)
        }}
        footer={null}
        destroyOnClose
        bodyStyle={{ paddingTop: 15 }}
      >
        {transcription}
      </Modal>
      <BreakdownModal ref={breakdownRef} />
    </>
  )
}

export default LeadReviewTable
